<template>
  <div class="flex h-full min-h-0 w-full flex-col gap-4">
    <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
      <section
        class="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
      >
        <h4 class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          {{ $t('global.informations') }}
        </h4>
        <BaseShowEditInput
          v-if="isFree"
          v-model="form.name"
          :label="$t('attributes.name')"
          name="name"
          :errors="errors.name"
          edition-mode
          required
        />
        <p v-else class="font-semibold">{{ item.document_reference }}</p>
        <BaseShowEditInput
          v-if="isFree"
          v-model="form.date"
          type="date"
          :label="$t('attributes.date')"
          name="date"
          :errors="errors.date"
          edition-mode
        />
        <BaseShowEditTextArea
          v-model="form.description"
          :label="$t('attributes.description')"
          name="description"
          :errors="errors.description"
          edition-mode
        />
      </section>

      <section
        v-if="calculation"
        class="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
        data-test="calculation"
      >
        <h4 class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          {{ $t('global.commission_base') }}
        </h4>
        <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt class="text-gray-500">
            {{ $t('attributes.commission_calculation_basis') }}
          </dt>
          <dd class="text-right font-medium">{{ calculation.label }}</dd>
          <template v-if="!calculation.perProduct">
            <dt class="text-gray-500">{{ $t('global.commission_base') }}</dt>
            <dd class="text-right font-medium">
              <AmountFormatter class="inline" :amount="item.base_amount" />
              <span v-if="item.pricing_basis" class="text-xs text-gray-500">
                ({{ item.pricing_basis }})
              </span>
            </dd>
            <dt class="text-gray-500">
              {{ $t('attributes.commission_rate_percent') }}
            </dt>
            <dd class="text-right font-medium">{{ calculation.rate }} %</dd>
            <dt class="text-gray-500">{{ $t('attributes.commission') }}</dt>
            <dd class="text-right font-medium">
              <AmountFormatter class="inline" :amount="calculation.amount" />
            </dd>
          </template>
        </dl>
        <ul
          v-if="calculation.baseLines.length"
          class="flex flex-col gap-1 text-xs text-gray-500"
          data-test="base-lines"
        >
          <li
            v-for="(line, index) in calculation.baseLines"
            :key="index"
            class="flex justify-between gap-4"
          >
            <span>{{ line.quantity }} × {{ line.name }}</span>
            <AmountFormatter :amount="line.base_amount" />
          </li>
        </ul>
        <ul
          v-if="calculation.excludedLines.length"
          class="flex flex-col gap-1 text-xs text-gray-400"
          data-test="excluded-lines"
        >
          <li
            v-for="(line, index) in calculation.excludedLines"
            :key="index"
            class="flex justify-between gap-4"
          >
            <span>
              {{ line.name }}
              <span class="italic">
                ({{
                  $t(
                    line.reason === 'third_party_cost'
                      ? 'global.third_party_cost'
                      : 'global.commission_document_excluded'
                  )
                }})
              </span>
            </span>
            <AmountFormatter :amount="line.base_amount" />
          </li>
        </ul>
        <div
          v-for="(product, index) in calculation.products"
          :key="index"
          class="flex justify-between gap-4 text-xs text-gray-500"
        >
          <span>
            {{ product.name }} — {{ product.rate }}
            {{ product.rate_type === 'pc' ? '%' : '€' }}
          </span>
          <AmountFormatter :amount="product.commission_htva" />
        </div>
        <p
          v-if="item.amount_edited"
          class="rounded bg-sky-50 px-3 py-2 text-sm text-sky-800"
          data-test="manual-amount"
        >
          <span class="font-semibold capitalize-first">
            {{ $t('global.commission_note_amount_edited') }}
          </span>
          :
          <AmountFormatter class="inline" :amount="item.commission_htva" />
        </p>
        <button
          v-if="item.amount_edited"
          type="button"
          class="w-fit text-sm font-medium text-primary hover:underline disabled:opacity-50"
          data-test="restore-calculation"
          :disabled="saving"
          @click="restore"
        >
          {{ $t('button.restore_automatic_amount') }}
        </button>
      </section>

      <section
        class="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
      >
        <h4 class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          {{ $t('attributes.amount') }}
        </h4>
        <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
          <template v-if="isFree">
            <BaseShowEditInput
              v-model="form.quantity"
              type="number"
              :label="$t('attributes.quantity')"
              name="quantity"
              :errors="errors.quantity"
              edition-mode
            />
            <BaseShowEditInput
              v-model="form.unit_price_htva"
              type="number"
              :label="$t('global.unit_price_excluding_vat')"
              name="unit_price_htva"
              :errors="errors.unit_price_htva"
              edition-mode
              required
            />
          </template>
          <BaseShowEditInput
            v-else
            v-model="form.commission_htva"
            type="number"
            :label="$t('attributes.commission')"
            name="commission_htva"
            :errors="errors.commission_htva"
            edition-mode
          />
          <SelectComponent
            v-model="form.vat_rate"
            :values="vatRates"
            :label="$t('global.tva')"
            name="vat_rate"
            :errors="errors.vat_rate"
            edition-mode
            :searchable="false"
          />
          <BaseShowEditInput
            :model-value="totalTvac"
            type="number"
            :label="$t('global.total_tvac')"
            name="total_tvac"
            data-test="total-tvac"
            edition-mode
            disabled
          />
        </div>
        <p
          v-if="!isFree && amountChanged"
          class="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
        >
          {{ $t('global.commission_note_amount_edit_warning') }}
        </p>
        <p v-if="isFree && amount !== null" class="text-right font-semibold">
          {{ $t('global.total') }} :
          <AmountFormatter class="inline" :amount="amount" />
        </p>
      </section>

      <section
        class="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600"
      >
        <BaseShowEditTextArea
          v-model="form.internal_note"
          :label="$t('attributes.internal_note')"
          name="internal_note"
          :errors="errors.internal_note"
          edition-mode
        />
      </section>
    </div>
    <div class="flex shrink-0 justify-end gap-3">
      <BaseButton
        theme-color="bg-secondary text-contrast-secondary"
        :disabled="saving"
        :title="$t('button.cancel')"
        @click="$emit('cancel')"
      />
      <BaseButton
        :disabled="saving"
        :display-loader="saving"
        :title="submitLabel ?? $t('button.save')"
        @click="save"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import AmountFormatter from '@c/AmountFormatter.vue'
import {
  freeLineAmount,
  itemPayload,
  itemToForm,
  withVat
} from '@/views/invoicing/show/commissionNote/components/commissionNoteItem'

/**
 * The form of a line of an open note: every field of a free line (to add or to
 * edit), only the description, internal note, VAT and amount of a calculated one.
 */
export default {
  name: 'CommissionNoteItemForm',
  components: {
    AmountFormatter,
    BaseButton,
    BaseShowEditInput,
    BaseShowEditTextArea,
    SelectComponent
  },
  props: {
    commissionNoteId: { type: [String, Number], required: true },
    /** The line to edit, nothing to add a free line */
    item: { type: Object, default: null },
    submitLabel: { type: String, default: null }
  },
  emits: ['saved', 'cancel'],
  data() {
    return {
      form: itemToForm(this.item),
      saving: false
    }
  },
  computed: {
    ...mapGetters({
      errors: 'auth/getErrors',
      allVatRates: 'metadata/getVatRatesMetadata',
      selectedVatRates: 'metadata/getSelectedVatRatesInSettingsMetadata',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    // how the commission of a calculated line was found, as it was frozen: still shown once the amount was typed by hand
    calculation() {
      // the detail the API sends, also once the amount was typed; the frozen conditions of the line for the older lines
      const detail = this.item?.calculation
      const calculation = detail ?? this.item?.details?.calculation

      if (this.isFree || !calculation?.basis) {
        return null
      }

      const options = this.initialMetadata?.enums?.commission_calculation_basis
      const perProduct = calculation.basis === 'per_product'
      const rate = calculation.rate_percent ?? 0

      return {
        label:
          (options ?? []).find((option) => option.id === calculation.basis)
            ?.name ?? calculation.basis,
        perProduct,
        rate,
        // the base and the rate give the amount the calculation found, whatever was typed since
        amount:
          Math.round((parseFloat(this.item.base_amount) || 0) * rate) / 100,
        products: perProduct ? (this.item.products ?? []) : [],
        baseLines: detail?.base_lines ?? [],
        excludedLines: detail?.excluded_lines ?? []
      }
    },
    isFree() {
      return !this.item || !!this.item.is_free
    },
    vatRates() {
      return (this.allVatRates ?? []).filter((rate) =>
        (this.selectedVatRates ?? []).includes(rate.id)
      )
    },
    amount() {
      return freeLineAmount(this.form)
    },
    // the amount with the VAT of the line, for information: it follows the commission (or the price) and the VAT being typed
    totalTvac() {
      const htva = this.isFree
        ? this.amount
        : parseFloat(this.form.commission_htva)

      if (
        htva === null ||
        Number.isNaN(htva) ||
        this.form.vat_rate === null ||
        this.form.vat_rate === ''
      ) {
        return ''
      }

      return withVat(htva, this.form.vat_rate)
    },
    amountChanged() {
      return itemPayload(this.item, this.form).commission_htva !== undefined
    }
  },
  methods: {
    ...mapActions({
      processStoreAction: 'commissionNote/storeCommissionNoteItem',
      processUpdateAction: 'commissionNote/updateCommissionNoteItem',
      processRestoreAction: 'commissionNote/restoreCommissionNoteItem'
    }),
    // back to the automatic calculation: the API answers the note with the line recalculated
    restore() {
      this.saving = true
      this.processRestoreAction({
        id: this.commissionNoteId,
        itemId: this.item.id
      })
        .then((response) => {
          this.$emit('saved', response.commission_note)
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false
        })
    },
    save() {
      this.saving = true
      const data = itemPayload(this.item, this.form)
      const request = this.item
        ? this.processUpdateAction({
            id: this.commissionNoteId,
            itemId: this.item.id,
            data
          })
        : this.processStoreAction({ id: this.commissionNoteId, data })

      request
        .then((response) => {
          this.$emit('saved', response.commission_note)
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>
